import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Input,
  Button,
  FormControl,
  FormLabel,
  Alert,
  IconButton,
  FormHelperText,
  Stack,
  LinearProgress
} from '@mui/joy';
import { Lock, CheckCircle, Eye, EyeSlash, ArrowLeft as ArrowBack } from '@phosphor-icons/react';
import { authApi } from '../../lib/api';

export const PasswordResetConfirmPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [handoff, setHandoff] = useState<{ ios_app_url: string; android_app_url: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password length
    if (newPassword.length < 12) {
      setError('Password must be at least 12 characters');
      setLoading(false);
      return;
    }

    if (!token) {
      setError('Invalid reset link');
      setLoading(false);
      return;
    }

    try {
      const result = await authApi.resetPassword(token, newPassword);
      setHandoff(result.mobile_handoff || null);
      setNewPassword('');
      setConfirmPassword('');
      setSuccess(true);
      // Keep the regular reset flow. Only the verified assisted account gets app next steps.
      if (!result.mobile_handoff) setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 2
        }}
      >
        <Card sx={{ maxWidth: 400, width: '100%', textAlign: 'center', p: 3 }}>
          <Alert color="danger" sx={{ mb: 2 }}>
            Invalid or missing reset token
          </Alert>
          <Button
            component={Link}
            to="/password-reset"
            variant="outlined"
            color="primary"
          >
            Request New Reset Link
          </Button>
        </Card>
      </Box>
    );
  }

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 2
        }}
      >
        <Card sx={{ maxWidth: 400, width: '100%', textAlign: 'center', p: 3 }}>
          <CheckCircle color="var(--joy-palette-success-500)" size={64} style={{ marginBottom: 16 }} />
          <Typography level="h2" sx={{ mb: 2 }}>
            {handoff ? "You're ready for the app" : 'Password Reset Successfully'}
          </Typography>
          <Typography level="body-md" sx={{ mb: 3, color: 'text.secondary' }}>
            {handoff
              ? 'Your password is saved. Open ApexOS and sign in with your email and your new password.'
              : 'Your password has been reset. Redirecting to login...'}
          </Typography>
          {handoff && (
            <Stack spacing={1.5}>
              <Button component="a" href="apexos:///" size="lg">Open ApexOS app</Button>
              {(handoff.ios_app_url || handoff.android_app_url) && (
                <Typography level="body-sm">Need to install it first? Choose your phone, then open the app and sign in.</Typography>
              )}
              {handoff.ios_app_url && <Button component="a" href={handoff.ios_app_url} variant="outlined">Get the iPhone app</Button>}
              {handoff.android_app_url && <Button component="a" href={handoff.android_app_url} variant="outlined">Get the Android app</Button>}
              <Button component={Link} to="/login" variant="plain">Continue in your browser</Button>
            </Stack>
          )}
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%', boxShadow: 'lg' }}>
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #7013f5 0%, #5a0fc8 100%)',
            color: 'white',
            p: 3,
            textAlign: 'center'
          }}
        >
          <Typography level="h2" sx={{ fontWeight: 300, fontSize: '1.5rem', mb: 1 }}>
            Set New Password
          </Typography>
          <Typography level="body-md" sx={{ opacity: 0.75 }}>
            Enter your new password below
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ p: 3 }}>
          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>New Password</FormLabel>
              <Input
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                startDecorator={<Lock />}
                endDecorator={<IconButton type="button" aria-label={showPassword ? 'Hide new password' : 'Show new password'} aria-pressed={showPassword} onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeSlash /> : <Eye />}</IconButton>}
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={loading}
              />
            </FormControl>

            <FormHelperText sx={{ mb: 2 }}>Use at least 12 characters, including uppercase and lowercase letters, a number, and a symbol. Try a few unrelated words; avoid common passwords and your personal details.</FormHelperText>
            <FormControl sx={{ mb: 3 }}>
              <FormLabel>Confirm Password</FormLabel>
              <Input
                type={showConfirmation ? 'text' : 'password'}
                autoComplete="new-password"
                startDecorator={<Lock />}
                endDecorator={<IconButton type="button" aria-label={showConfirmation ? 'Hide repeated password' : 'Show repeated password'} aria-pressed={showConfirmation} onClick={() => setShowConfirmation(!showConfirmation)}>{showConfirmation ? <EyeSlash /> : <Eye />}</IconButton>}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
              />
            </FormControl>

            <Button
              type="submit"
              fullWidth
              size="lg"
              loading={loading}
              sx={{
                background: 'linear-gradient(135deg, #7013f5 0%, #5a0fc8 100%)',
                mb: 2,
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a0fc8 0%, #4a0da8 100%)',
                }
              }}
            >
              Reset Password
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Button
                component={Link}
                to="/login"
                variant="plain"
                startDecorator={<ArrowBack />}
                size="sm"
              >
                Back to Login
              </Button>
            </Box>
          </form>
        </Box>
      </Card>
    </Box>
  );
};
